import type { MessageType } from "mavlink.ts";
import { createMavlinkChannel } from "mavlink.ts";

import type { Schema } from "../gen/schema";
import { schema } from "../gen/schema";
import type { Channel } from "./channel";
import { delay } from "./common";

export const createMavlink = (channel: Channel<Uint8Array>) => {
  const mavlink = createMavlinkChannel(channel, schema);

  const close = () => {
    mavlink.close();
    channel.close();
  };

  const send = <T extends keyof Schema>(
    type: T,
    message: MessageType<Schema, T>,
  ) => mavlink.send({ systemId: 255, componentId: 0, type, message });

  const { receive } = mavlink;

  const receiveOne = <T extends keyof Schema>(
    type: T,
    condition: (_: MessageType<Schema, T>) => boolean = () => true,
    cancel?: Promise<never>,
  ) =>
    Promise.race([
      ...(cancel ? [cancel] : []),
      new Promise<MessageType<Schema, T>>(resolve =>
        mavlink.receive(_ => {
          if (type !== _.type) return;
          const message = _.message as MessageType<Schema, T>;
          if (condition(message)) resolve(message);
        }),
      ),
    ]);

  const retry = async <T extends keyof Schema, R>(
    type: T,
    message: MessageType<Schema, T>,
    received?: Promise<R>,
    cancel?: Promise<never>,
  ) => {
    const timeout = 500;
    for (let i = 0; ; i++) {
      send(type, message);
      const wait = Math.min(10000, timeout * 1.25 ** i);
      const result = await Promise.race([
        received,
        delay(wait),
        ...(cancel ? [cancel] : []),
      ]);
      if (result) return result;
      console.log("Retry", { type });
    }
  };

  return {
    send,
    receive,
    receiveOne,
    retry,
    close,
  };
};

export type Mavlink = ReturnType<typeof createMavlink>;
